import { CommonModule, NgClass, NgIf } from '@angular/common';
import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { UserAvatarDialogComponent } from '../../components/dialog/Profile/userAvatar-dialog.component';
import { InputComponent } from '../../components/from-input/input.component';
import { CustomToastComponent } from '../../components/toast/custom-toast.component';
import { HttpService } from '../../services/http.service';
import { UserProfileService } from '../../services/user-profile.service';
import { tap } from 'rxjs';
import { userRoles } from '../../../../environments/environment';

interface CSQTeamMember {
  id: any;
  memberId: string;
  firstName: string;
  lastName: string;
  email: string;
  mobileNumber: string;
  secondaryemail: string;
  schoolName: string;
  dateOfBirth?: string;
  schoolEmail?: string;
  parentGuardianFullName?: string;
  parentEmail?: string;
  yearLevel?: string;
  gender?: string;
  ethnicity?: string;
  personalEmail?: string;
  past?: Array<{ courseId: string }>; // Changed to array of objects
  comp?: Array<{ courseId: string }>;
}

interface APIResponse {
  status: boolean;
  message: string;
  data: CSQTeamMember[];
}

function dobNotInFuture(control: AbstractControl): ValidationErrors | null {
  if (!control.value) return null;

  const selectedDate = new Date(control.value);
  const today = new Date();

  selectedDate.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);

  return selectedDate > today ? { futureDob: true } : null;
}

@Component({
  selector: 'app-profile',
  standalone: true,
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.scss'],
  imports: [
    ReactiveFormsModule,
    NgClass,
    NgIf,
    CommonModule,
    InputComponent,
    MatDialogModule,
  ],
})
export class ProfileComponent implements OnInit {
  @Input() context: 'profile' | 'password' | 'default' = 'default';
  @ViewChild('fileInput') fileInput!: ElementRef;

  profileImageFile: File | null = null;
  loading: boolean = false;
  profileForm!: FormGroup;
  studentForm!: FormGroup;
  profileImage: string = '';
  isEditMode: boolean = false;
  userID: string = '';
  useAvatarImage: boolean = false;
  isSubmitting = false;
  userInitials: string = '';

  genderOptions = [
    { label: 'Male', value: 'male' },
    { label: 'Female', value: 'female' },
    { label: 'Non-identifying', value: 'non-identifying' },
    { label: 'Prefer not to say', value: 'prefernottosay' },
  ];
  dataOption: any[] = [
    { label: 'Yes', value: 'yes' },
    { label: 'No', value: 'no' },
  ];
  showConstructionRelatedCourseAnswers: boolean = false;
  showConstructionCourseAnswers: boolean = false;

  constructionPastAnswers: any[] = [];
  constructionCompAnswers: any[] = [];
  yearLevelOption: any[] = [];
  ethnicityOption: any[] = [];

  customErrorMessages = {
    firstName: {
      required: 'Please enter a first name.',
      whitespace: 'Please enter a first name.',
    },
    lastName: {
      required: 'Please enter a last name.',
      whitespace: 'Please enter a last name.',
    },
    email: {
      required: 'Please enter a email address.',
      whitespace: 'Please enter a email address.',
      email: 'Please enter a valid email',
    },
    secondaryemail: {
      email: 'Please enter a valid email',
      // whitespace: 'Please enter a valid email',
    },
    mobileNumber: {
      required: 'Please enter a phone number.',
      whitespace: 'Please enter a phone number.',
      pattern: 'Please enter a valid phone number.',
    },
    schoolName: {
      required: 'School name is required.',
      whitespace: 'School name is required.',
    },
    dateOfBirth: {
      required: 'Date of birth is required.',
      whitespace: 'Date of birth is required.',
      futureDob: 'Date of birth cannot be a future date.',
    },
    schoolEmail: {
      required: 'School email is required.',
      whitespace: 'School email is required',
      email: 'Please enter a valid email.',
    },
    parentGuardianFullName: {
      required: 'Parent/Guardian name is required.',
      whitespace: 'Parent/Guardian name is required.',
    },
    parentEmail: {
      required: 'Parent/Guardian email is required',
      whitespace: 'Parent/Guardian email is required.',
      email: 'Please enter a valid email',
    },
    yearLevel: {
      required: 'Please enter a year level.',
      whitespace: 'Please enter a year level.',
      pattern: 'Please enter a year level in numeric.',
      min: 'Please enter a year level must be at least 1',
      max: 'Please enter a year level must be 12 or less',
    },

    gender: {
      required: 'Gender is required',
      whitespace: 'Gender is required.',
    },
    ethnicity: {
      required: 'Ethnicity is required',
      whitespace: 'Ethnicity is required.',
    },
    personalEmail: {
      email: 'Please enter a valid email',
      whitespace: 'Please enter a valid email.',
    },
  };

  userRoles = {
    manage: {
      view: false,
      edit: false,
    },
    reset: {
      view: false,
      edit: false,
    },
  };
  Role = localStorage.getItem('userRole') || '';
  private isProfileImageInitialized = false;

  constructor(
    private fb: FormBuilder,
    private httpService: HttpService,
    private snackBar: MatSnackBar,
    private userProfileService: UserProfileService,
    private dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    const userDetailsString = localStorage.getItem('userDetails');
    if (userDetailsString) {
      const userDetails = JSON.parse(userDetailsString);
      this.userID = userDetails.userId;
    }

    const user = localStorage.getItem('userRole');
    if (user && user in userRoles) {
      this.userRoles = userRoles[user as keyof typeof userRoles]?.settings;
    }
    this.initForms();
    this.setupConstructionCourseListener();
    this.setupConstructionRelatedCourseListener();

    if (user === 'student') {
      this.fetchPastAnswers().subscribe();
      this.fetchCompAnswers().subscribe();
      this.fetchEthnicityDropDown().subscribe();
      this.fetchYearLevelDropDown().subscribe();
    }
    this.fetchUserData();
    this.setupRealTimeValidation();
  }

  get isAdminOrTeacher(): boolean {
    return ['csqadmin', 'csquser', 'superadmin', 'teacher'].includes(this.Role);
  }

  get isStudent(): boolean {
    return this.Role === 'student';
  }

  get displayName(): string {
    const currentForm = this.isStudent ? this.studentForm : this.profileForm;
    const firstName = currentForm?.get('firstName')?.value || '';
    const lastName = currentForm?.get('lastName')?.value || '';
    return `${firstName} ${lastName}`.trim() || '...';
  }

  get displayInitials(): string {
    const currentForm = this.isStudent ? this.studentForm : this.profileForm;
    const firstName = currentForm?.get('firstName')?.value || '';
    const lastName = currentForm?.get('lastName')?.value || '';

    if (firstName && lastName) {
      return `${firstName.charAt(0).toUpperCase()}${lastName.charAt(0).toUpperCase()}`;
    } else if (firstName) {
      return firstName.charAt(0).toUpperCase();
    } else if (lastName) {
      return lastName.charAt(0).toUpperCase();
    }
    return '...';
  }

  get displayRole(): string {
    const roleMap: { [key: string]: string } = {
      superadmin: 'Super Admin',
      csqadmin: 'CSQ Admin',
      student: 'Student',
      teacher: 'Teacher',
    };
    return roleMap[this.Role] || '';
  }

  get displayedProfileImage(): string {
    return this.profileImage || 'assets/images/avatar-default.png';
  }
  private noEmptyOrWhitespaceValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (
        control.value === null ||
        control.value === undefined ||
        control.value === ''
      ) {
        return { required: true };
      }
      if (
        typeof control.value === 'string' &&
        control.value.trim().length === 0
      ) {
        return { whitespace: true };
      }

      return null;
    };
  }

  initForms(): void {
    this.profileForm = this.fb.group({
      firstName: [
        '',
        [Validators.required, this.noEmptyOrWhitespaceValidator()],
      ],
      lastName: [
        '',
        [Validators.required, this.noEmptyOrWhitespaceValidator()],
      ],
      email: [
        '',
        [
          Validators.required,
          Validators.pattern(
            /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
          ),
          this.noEmptyOrWhitespaceValidator(),
        ],
      ],
      secondaryemail: [
        '',
        [
          Validators.pattern(
            /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
          ),
        ],
      ],
      mobileNumber: [
        '',
        [
          Validators.pattern(/^(?:0[47]\d{8}|\+61 ?[47]\d{8})$/),
        ],
      ],
      schoolName: [
        '',
        this.Role === 'teacher'
          ? [Validators.required, this.noEmptyOrWhitespaceValidator()]
          : [],
      ],
    });

    this.studentForm = this.fb.group({
      firstName: [
        '',
        [Validators.required, this.noEmptyOrWhitespaceValidator()],
      ],
      lastName: [
        '',
        [Validators.required, this.noEmptyOrWhitespaceValidator()],
      ],
      schoolName: [
        '',
        [Validators.required, this.noEmptyOrWhitespaceValidator()],
      ],
      dateOfBirth: [
        '',
        [
          Validators.required,
          this.noEmptyOrWhitespaceValidator(),
          dobNotInFuture,
        ],
      ],
      schoolEmail: [
        '',
        [
          Validators.required,
          Validators.pattern(
            /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
          ),
          this.noEmptyOrWhitespaceValidator(),
        ],
      ],
      parentGuardianFullName: [
        '',
        [Validators.required, this.noEmptyOrWhitespaceValidator()],
      ],
      parentEmail: [
        '',
        [
          Validators.required,
          Validators.pattern(
            /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
          ),
          this.noEmptyOrWhitespaceValidator(),
        ],
      ],
      yearLevel: [''],
      gender: [''],
      ethnicity: [''],
      secondaryemail: [
        '',
        [
          Validators.pattern(
            /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
          ),
          // this.noEmptyOrWhitespaceValidator()
        ],
      ],
      constructionCourse: [''],
      constructionRelatedCourse: [''],
      past: [[]],
      comp: [[]],
    });

    if (this.context === 'profile') {
      this.disableForms();
    }
    this.profileForm.get('email')?.disable();
    this.profileForm.get('schoolName')?.disable();
    this.studentForm.get('schoolEmail')?.disable();
  }

  private setupRealTimeValidation(): void {
    Object.keys(this.profileForm.controls).forEach((controlName) => {
      const control = this.profileForm.get(controlName);
      if (control) {
        control.valueChanges.subscribe(() => {
          if (control.invalid && (control.dirty || control.touched)) {
            control.markAsTouched();
          }
        });
      }
    });
    Object.keys(this.studentForm.controls).forEach((controlName) => {
      const control = this.studentForm.get(controlName);
      if (control) {
        control.valueChanges.subscribe(() => {
          if (control.invalid && (control.dirty || control.touched)) {
            control.markAsTouched();
          }
        });
      }
    });
  }

  fetchUserData(): void {
    this.loading = true;
    this.httpService.get<any>(`settings/profile/${this.userID}`).subscribe({
      next: ({ data }) => {
        if (data) {
          this.populateFormData(data);
          this.loading = false;
          // if (!this.isProfileImageInitialized) {
          this.initializeProfileImage(data.profilePicture);
          // }

          if (this.context === 'profile' && !this.isEditMode) {
            this.disableForms();
          }

          this.profileImageFile = null;
          this.useAvatarImage = false;
        } else {
          this.loading = false;
          console.error('No data received from API');
          this.showErrorToast('Something went wrong. Please try again later.');
        }
      },
      error: (error) => {
        console.error('Error fetching user data:', error);
        this.loading = false;
        // this.showErrorToast('Error loading profile data');
        this.showErrorToast(error?.message);
      },
    });
  }

  private populateFormData(user: any): void {
    if (this.isAdminOrTeacher) {
      this.populateAdminTeacherForm(user);
    } else if (this.isStudent) {
      this.populateStudentForm(user);
    }
  }

  private populateAdminTeacherForm(user: any): void {
    const formValues: any = {
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      email: user.email || '',
      secondaryemail: user.secondaryEmail || '',
      schoolName: user.schoolName || '',
    };
    if (user.mobileNumber) {
      const mobileNumber = user.mobileNumber.toString();
      formValues.mobileNumber = mobileNumber.padStart(10, '0');
    } else {
      formValues.mobileNumber = '';
    }
    this.profileForm.patchValue(formValues, { emitEvent: false });
    this.profileForm.get('email')?.disable({ emitEvent: false });
  }

  isCourseChecked(controlName: 'past' | 'comp', courseId: string): boolean {
    const values: Array<{ courseId: string }> =
      this.studentForm.get(controlName)?.value ?? [];
    return values.some((item) => item.courseId === courseId);
  }

  private populateStudentForm(user: any): void {
    const pastCourseIds =
      user.past
        ?.filter((item: any) => item.value === true)
        .map((item: any) => item.courseId) ||
      user.pastAnswers?.map((item: any) => item.courseId || item) ||
      [];

    const compCourseIds =
      user.comp
        ?.filter((item: any) => item.value === true)
        .map((item: any) => item.courseId) ||
      user.completedAnswers?.map((item: any) => item.courseId || item) ||
      [];

    const formValues: any = {
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      dateOfBirth: user.dateOfBirth || '',
      schoolEmail: user.schoolEmail || '',
      schoolName: user.schoolName || '',
      parentGuardianFullName: user.parentGuardianFullName || '',
      parentEmail: user.parentGuardianEmail || '',
      yearLevel: user.yearLevelId || '',
      gender: user.gender || '',
      ethnicity: user.ethnicityId || '',
      secondaryemail: user.personalEmail || '',
      constructionCourse:
        pastCourseIds.length > 0 ? 'yes' : user.constructionCourse || 'no',
      constructionRelatedCourse:
        compCourseIds.length > 0
          ? 'yes'
          : user.constructionRelatedCourse || 'no',
      // Store as {courseId} objects so onSelectChange stays consistent
      past: pastCourseIds.map((id: string) => ({ courseId: id })),
      comp: compCourseIds.map((id: string) => ({ courseId: id })),
    };

    this.studentForm.patchValue(formValues, { emitEvent: false });
    this.studentForm.get('schoolEmail')?.disable({ emitEvent: false });
    this.studentForm.get('schoolName')?.disable({ emitEvent: false });
    this.studentForm.get('firstName')?.disable({ emitEvent: false });
    this.studentForm.get('lastName')?.disable({ emitEvent: false });
    this.studentForm.get('mobileNumber')?.disable({ emitEvent: false });
    this.studentForm.get('dateOfBirth')?.disable({ emitEvent: false });
    this.studentForm
      .get('parentGuardianFullName')
      ?.disable({ emitEvent: false });
    this.studentForm.get('parentEmail')?.disable({ emitEvent: false });

    // Use derived IDs (not raw API field) — consistent with constructionCourse value above
    this.showConstructionCourseAnswers = pastCourseIds.length > 0;
    this.showConstructionRelatedCourseAnswers = compCourseIds.length > 0;
  }

  private initializeProfileImage(profilePicture: string): void {
    if (profilePicture) {
      this.profileImage = profilePicture;
      this.userProfileService.setProfileImage(this.profileImage);
    } else {
      this.profileImage = '';
      this.userProfileService.setProfileImage(
        'assets/images/avatar-default.png',
      );
    }
    this.isProfileImageInitialized = true;
  }

  enableEdit(): void {
    if (this.context === 'profile') {
      this.isEditMode = true;
      this.enableForms();
      this.markFormGroupUntouched(
        this.isStudent ? this.studentForm : this.profileForm,
      );
    }
  }

  onFileSelected(event: any): void {
    if (this.context !== 'profile' || this.isEditMode) {
      const file = event.target.files?.[0];
      if (!file) return;

      if (!this.validateFile(file)) return;

      this.profileImageFile = file;
      this.useAvatarImage = false;

      this.readAndSetProfileImage(file);
    }
  }

  setupConstructionCourseListener(): void {
    this.studentForm
      .get('constructionCourse')
      ?.valueChanges.subscribe((value) => {
        this.showConstructionCourseAnswers = value === 'yes';
      });
  }

  setupConstructionRelatedCourseListener(): void {
    this.studentForm
      .get('constructionRelatedCourse')
      ?.valueChanges.subscribe((value) => {
        this.showConstructionRelatedCourseAnswers = value === 'yes';
      });
  }
  private validateFile(file: File): boolean {
    const allowedTypes = ['image/jpeg', 'image/png'];
    const maxSizeInMB = 5;

    if (!allowedTypes.includes(file.type)) {
      this.showErrorToast('Only JPEG and PNG images are allowed');
      return false;
    }

    if (file.size > maxSizeInMB * 1024 * 1024) {
      this.showErrorToast('Image size must not exceed 5MB');
      return false;
    }

    return true;
  }

  private readAndSetProfileImage(file: File): void {
    const reader = new FileReader();
    reader.onload = (e: any) => {
      this.profileImage = e.target.result;
      this.userProfileService.setProfileImage(this.profileImage);
      this.isProfileImageInitialized = true;
    };
    reader.onerror = () => {
      this.showErrorToast('Error reading image file');
    };
    reader.readAsDataURL(file);
  }

  uploadNewPicture(): void {
    if (this.context !== 'profile' || this.isEditMode) {
      if (this.Role === 'student') {
        this.openAvatarDialog();
      } else {
        this.fileInput.nativeElement.click();
      }
    }
  }

  private openAvatarDialog(): void {
    const dialogRef = this.dialog.open(UserAvatarDialogComponent, {
      width: '600px',
      disableClose: true,
    });

    dialogRef
      .afterClosed()
      .subscribe((selectedImage: { imageUrl: string } | null) => {
        if (selectedImage?.imageUrl) {
          this.profileImage = selectedImage.imageUrl;
          this.userProfileService.setProfileImage(this.profileImage);
          this.profileImageFile = null;
          this.useAvatarImage = true;
          this.isProfileImageInitialized = true;
        }
      });
  }

  deleteProfilePicture(): void {
    if (this.context !== 'profile' || this.isEditMode) {
      this.profileImage = '';
      this.userProfileService.setProfileImage(
        'assets/images/avatar-default.png',
      );
      this.profileImageFile = null;
      this.useAvatarImage = false;
      this.isProfileImageInitialized = true;
    }
  }

  onSave(): void {
    const currentForm = this.isStudent ? this.studentForm : this.profileForm;

    if (this.isStudent) {
      const constructionCourseValue =
        this.studentForm.get('constructionCourse')?.value;
      const constructionRelatedValue = this.studentForm.get(
        'constructionRelatedCourse',
      )?.value;
      const pastAnswers: Array<{ courseId: string }> =
        this.studentForm.get('past')?.value || [];
      const compAnswers: Array<{ courseId: string }> =
        this.studentForm.get('comp')?.value || [];

      if (constructionCourseValue === 'yes' && pastAnswers.length === 0) {
        this.showErrorToast(
          'Please select at least one construction-related course from the past 12 months',
        );
        return;
      }

      if (constructionRelatedValue === 'yes' && compAnswers.length === 0) {
        this.showErrorToast(
          'Please select at least one construction related course',
        );
        return;
      }
    }

    if (currentForm.valid) {
      this.saveProfileData();
    } else {
      this.markFormGroupTouched(currentForm);
    }
  }

  onSelectChange(event: any, question: 1 | 2): void {
    const courseId = event.target.value;
    const checked = event.target.checked;
    const controlName = question === 1 ? 'past' : 'comp';
    const current: Array<{ courseId: string }> =
      this.studentForm.get(controlName)?.value ?? [];

    const updated = checked
      ? [...current, { courseId }]
      : current.filter((item) => item.courseId !== courseId);

    this.studentForm.get(controlName)?.setValue(updated);
  }

  updateLocalStorageInfo() {
    const currentForm = this.isStudent ? this.studentForm : this.profileForm;
    const rawValues = currentForm.getRawValue();

    this.userProfileService.setUserDetails({
      name: `${rawValues.firstName} ${rawValues.lastName}`.trim(),
      firstName: rawValues.firstName,
      lastName: rawValues.lastName,
      avatar: this.profileImage,
    });
  }

  private saveProfileData(): void {
    if (this.isSubmitting) return;
    this.isSubmitting = true;
    const formData = this.prepareFormData();
    const apiUrl = `settings/update-profile/${this.userID}`;

    this.httpService.put<APIResponse>(apiUrl, formData, true).subscribe({
      next: (response) => {
        if (response?.status) {
          this.showSuccessToast(response.message);
          this.updateLocalStorageInfo();
          this.isEditMode = false;
          this.isSubmitting = false;
          if (this.context === 'profile') {
            this.disableForms();
          }
        } else {
          this.showErrorToast(response?.message || 'Profile update failed');
          this.isSubmitting = false;
        }
      },
      error: (err) => {
        this.isSubmitting = false;
        console.error('Update error:', err);
        this.showErrorToast('Profile update failed. Please try again.');
      },
    });
  }

  private prepareFormData(): FormData {
    const formData = new FormData();
    const currentForm = this.isStudent ? this.studentForm : this.profileForm;

    Object.keys(currentForm.controls).forEach((key) => {
      const control = currentForm.get(key);

      // Skip past/comp — handled separately below
      if (key === 'past' || key === 'comp') return;

      if (
        control?.enabled &&
        control.value !== null &&
        control.value !== undefined
      ) {
        if (Array.isArray(control.value)) {
          formData.append(key, JSON.stringify(control.value));
        } else {
          formData.append(key, control.value.toString());
        }
      }
    });

    if (this.isStudent) {
      const formValues = currentForm.value;
      const constructionCourseValue =
        currentForm.get('constructionCourse')?.value;
      const constructionRelatedValue = currentForm.get(
        'constructionRelatedCourse',
      )?.value;

      // Get selected courseIds from {courseId} objects stored in past/comp
      const selectedPastIds: string[] = (
        currentForm.get('past')?.value ?? []
      ).map((item: { courseId: string }) => item.courseId);
      const selectedCompIds: string[] = (
        currentForm.get('comp')?.value ?? []
      ).map((item: { courseId: string }) => item.courseId);

      // Build past array like AddStudentComponent.buildPayload()
      const pastCourses = this.constructionPastAnswers.map((course) => ({
        courseId: course.id,
        courseName: course.courseName,
        value:
          constructionCourseValue === 'yes'
            ? selectedPastIds.includes(course.id)
            : false,
      }));

      const compCourses = this.constructionCompAnswers.map((course) => ({
        courseId: course.id,
        courseName: course.courseName,
        value:
          constructionRelatedValue === 'yes'
            ? selectedCompIds.includes(course.id)
            : false,
      }));

      formData.append('past', JSON.stringify(pastCourses));
      formData.append('comp', JSON.stringify(compCourses));
    }

    if (this.profileImageFile && !this.useAvatarImage) {
      formData.append('profileImage', this.profileImageFile);
      formData.append('profileImageUrl', '');
    } else if (this.useAvatarImage && this.profileImage) {
      formData.append('profileImageUrl', this.profileImage);
    } else {
      formData.append('profileImageUrl', '');
    }

    formData.append('existingProfileImageUrl', this.profileImage ?? '');

    return formData;
  }

  onCancel(): void {
    this.fetchUserData();
    if (this.context === 'profile') {
      this.disableForms();
    }
    this.isEditMode = false;
    this.resetProfileImageState();
  }

  private resetProfileImageState(): void {
    this.profileImageFile = null;
    this.useAvatarImage = false;
    this.isProfileImageInitialized = false;
  }

  isControlEnabled(controlName: string): boolean {
    if (this.isStudent) {
      const editableControls = [
        'constructionCourse',
        'constructionRelatedCourse',
        'yearLevel',
        'gender',
        'ethnicity',
        'secondaryemail',
      ];
      if (editableControls.includes(controlName)) {
        return this.isEditMode;
      }
      return false;
    }
    const currentForm = this.isStudent ? this.studentForm : this.profileForm;
    const control = currentForm.get(controlName);
    return control ? control.enabled : false;
  }

  private enableForms(): void {
    if (this.isStudent) {
      this.studentForm.enable();
      this.studentForm.get('schoolEmail')?.disable();
      this.studentForm.get('schoolName')?.disable();
      this.studentForm.get('firstName')?.disable();
      this.studentForm.get('lastName')?.disable();
      this.studentForm.get('mobileNumber')?.disable();
      this.studentForm.get('dateOfBirth')?.disable();
      this.studentForm.get('parentGuardianFullName')?.disable();
      this.studentForm.get('parentEmail')?.disable();
      this.studentForm.get('constructionCourse')?.enable();
      this.studentForm.get('constructionRelatedCourse')?.enable();
      this.studentForm.get('yearLevel')?.enable();
      this.studentForm.get('gender')?.enable();
      this.studentForm.get('ethnicity')?.enable();
      this.studentForm.get('secondaryemail')?.enable();
    } else {
      this.profileForm.enable();
      this.profileForm.get('email')?.disable();
      this.profileForm.get('schoolName')?.disable();
    }
  }

  private disableForms(): void {
    if (this.isStudent) {
      this.studentForm.disable();
    } else {
      this.profileForm.disable();
    }
  }

  fetchCompAnswers() {
    return this.httpService
      .get<any>(`studentdashboard/con-rel-course-comp`)
      .pipe(
        tap({
          next: (response) => {
            if (Array.isArray(response.data)) {
              this.constructionCompAnswers = response.data;
            } else {
              this.showErrorToast('Invalid event type data format');
            }
          },
          error: (err) => {
            this.showErrorToast(err?.message);
          },
        }),
      );
  }

  fetchPastAnswers() {
    return this.httpService
      .get<any>(`studentdashboard/con-rel-course-past`)
      .pipe(
        tap({
          next: (response) => {
            if (Array.isArray(response.data)) {
              this.constructionPastAnswers = response.data;
            } else {
              this.showErrorToast('Invalid event type data format');
            }
          },
          error: (err) => {
            this.showErrorToast(err?.message);
          },
        }),
      );
  }

  fetchYearLevelDropDown() {
    const type = 2;
    const status = true;
    return this.httpService
      .get<any>(`settings/yearlevel/dropdown?status=${status}`)
      .pipe(
        tap({
          next: (response) => {
            if (Array.isArray(response.data)) {
              const activeEventTypes = response.data.filter(
                (item: any) => item.status === 'Active',
              );
              this.yearLevelOption = response.data.map(
                (item: { name: any; id: any }) => ({
                  label: item.name,
                  value: item.id,
                }),
              );
            } else {
              this.showErrorToast('Invalid event type data format');
            }
          },
          error: (err) => {
            this.showErrorToast(err?.message);
          },
        }),
      );
  }

  fetchEthnicityDropDown() {
    const type = 2;
    const status = true;
    return this.httpService
      .get<any>(`settings/ethnicity/dropdown?status=${status}`)
      .pipe(
        tap({
          next: (response) => {
            if (Array.isArray(response.data)) {
              const activeEventTypes = response.data.filter(
                (item: any) => item.status === 'Active',
              );
              this.ethnicityOption = response.data.map(
                (item: { name: any; id: any }) => ({
                  label: item.name,
                  value: item.id,
                }),
              );
            } else {
              this.showErrorToast('Invalid event type data format');
            }
          },
          error: (err) => {
            this.showErrorToast(err?.message);
          },
        }),
      );
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.get(key);
      control?.markAsTouched();
    });
  }

  private markFormGroupUntouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.get(key);
      control?.markAsUntouched();
    });
  }

  private showSuccessToast(message: string): void {
    this.snackBar.openFromComponent(CustomToastComponent, {
      data: { message },
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: ['no-padding-snackbar', 'success-toast'],
    });
  }

  private showErrorToast(message: string | null | undefined): void {
    if (!message) return;
    this.snackBar.openFromComponent(CustomToastComponent, {
      data: { message },
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: ['no-padding-snackbar', 'error-toast'],
    });
  }
}
